import type { FC } from "react";
import type { UseFieldArrayReturn, UseFormReturn } from "react-hook-form";
import {
  caseToOperateThunks,
  CTOTitle as Title,
  FieldsArray,
  FileTypes,
  FormDraftButton,
  getMitigationDefault,
  PrimaryButton,
  Response,
  ResponsePostCto,
  translate,
} from "@shape/reef-shared";
import type { FileProps, StepProps } from "@shape/reef-shared";
import { FilesField } from "components/Form";
import { AssigningRecommendation } from "pages/cto/pages/CtoDetails/components/Step2Evaluation/AssigningRecommendation/AssigningRecommendation";
import { RiskLevelWithMatrixAndComment } from "pages/cto/pages/CtoDetails/components/Step2Evaluation/RiskLevel/RiskLevelWithMatrixAndComment.old";
import type { CreateStep2Body, CreateStep2MdbFormValues, Step2Response } from "types";
import styles from "./ProcessSafetyComplement.module.scss";

interface ProcessSafetyComplementProps {
  cto: StepProps<Step2Response>["cto"];
  ctoId: string;
  createdStep2: Response<ResponsePostCto>;
  fieldsArrayControl: UseFieldArrayReturn<CreateStep2MdbFormValues, "assigning_recommendations">;
  flaggedQuestions: boolean;
  form: UseFormReturn<CreateStep2MdbFormValues>;
  handleDraftSubmit: () => void;
  initialValues: Step2Response;
  isCurrentVersion: boolean;
  onUploadSuccess: () => void;
  readOnly: boolean;
  submitDisabled: boolean;
  vesselId: string;
}

export const ProcessSafetyComplement: FC<ProcessSafetyComplementProps> = ({
  cto,
  ctoId,
  createdStep2,
  fieldsArrayControl,
  flaggedQuestions,
  form,
  handleDraftSubmit,
  initialValues,
  isCurrentVersion,
  onUploadSuccess,
  readOnly,
  submitDisabled,
  vesselId,
}) => {
  const riskForm = form as unknown as UseFormReturn<CreateStep2Body>;

  const downloadFile = (file: FileProps) =>
    caseToOperateThunks.downloadFile({
      attachment_id: file.attachment_id,
      file_name: file.file_name,
      vesselId,
      ctoId,
      shouldUseHistory: !isCurrentVersion,
    });

  return (
    <>
      <RiskLevelWithMatrixAndComment
        form={riskForm}
        fieldName="risk_level"
        commentFieldName="risk_level_justification"
        commentDefaultValue={initialValues.risk_level_justification}
        readOnly={readOnly}
        riskLevelDefaultValue={initialValues.risk_level ?? null}
        sectionTitle=""
        selectKey="processSafetyInitialRiskLevel"
        selectLabel={translate("ctoStep2.initialRiskLevel")}
        withComment={false}
      >
        <div className={styles.section}>
          {!readOnly && <Title>{translate("ctoStep1.uploadAndAttachFiles")}</Title>}
          <FilesField
            form={form}
            borderWidth={2}
            name="risk_level_files"
            readOnly={readOnly}
            defaultValue={initialValues.risk_level_files}
            downloadFile={downloadFile}
            uploadFiles={(files: File[]) =>
              caseToOperateThunks.uploadFiles(
                vesselId,
                ctoId,
                onUploadSuccess,
                files,
                FileTypes.EVALUATION
              )
            }
            vesselId={vesselId}
            ctoId={ctoId}
          />
        </div>
      </RiskLevelWithMatrixAndComment>

      <div className={styles.recommendations}>
        <FieldsArray
          control={fieldsArrayControl}
          form={form}
          name="assigning_recommendations"
          label={translate("ctoStep2.assigningRecommendation")}
          defaultValue={readOnly ? initialValues.assigning_recommendations : [{}]}
          readOnly={readOnly}
          showAddButtonName={translate("ctoStep2.addNewRecommendation")}
          renderRow={(prefix, _field, index) => (
            <AssigningRecommendation
              form={form}
              initialValues={
                initialValues.assigning_recommendations[index] || getMitigationDefault()
              }
              readOnly={readOnly}
              prefix={prefix}
              index={index}
              vesselId={vesselId}
              tags={cto.tags ?? []}
              isNewCto={true}
              hasTagsSelectField={true}
              flaggedQuestions={flaggedQuestions}
            />
          )}
        />
      </div>

      <RiskLevelWithMatrixAndComment
        commentFieldName="risk_level_after_measures_comment"
        commentDefaultValue={initialValues.risk_level_after_measures_comment}
        form={riskForm}
        fieldName="risk_level_after_measures"
        readOnly={readOnly}
        riskLevelDefaultValue={initialValues.risk_level_after_measures ?? null}
        sectionTitle=""
        selectKey="processSafetyResidualRiskLevel"
        selectLabel={translate("ctoStep2.residualRiskLevel")}
        withComment={true}
      >
        <div className={styles.section}>
          {!readOnly && <Title>{translate("ctoStep1.uploadAndAttachFiles")}</Title>}
          <FilesField
            form={form}
            borderWidth={2}
            name="risk_level_after_measures_files"
            readOnly={readOnly}
            defaultValue={initialValues.risk_level_after_measures_files}
            downloadFile={downloadFile}
            uploadFiles={(files: File[]) =>
              caseToOperateThunks.uploadFiles(
                vesselId,
                ctoId,
                onUploadSuccess,
                files,
                FileTypes.JUSTIFICATION_RESIDUAL_RISK
              )
            }
            vesselId={vesselId}
            ctoId={ctoId}
          />
        </div>
      </RiskLevelWithMatrixAndComment>

      {!readOnly && (
        <div className={styles.actions}>
          <PrimaryButton
            type="submit"
            disabled={createdStep2.loading || submitDisabled}
            onClick={() => form.setValue("draft", false)}
          >
            {createdStep2.loading && !form.getValues("draft")
              ? translate("formGeneric.submitting")
              : translate("formGeneric.submitForm")}
          </PrimaryButton>

          <FormDraftButton
            form={form}
            disabled={submitDisabled}
            readOnly={readOnly}
            isSubmitting={createdStep2.loading}
            handleSubmit={handleDraftSubmit}
          />
        </div>
      )}
    </>
  );
};
